import React, { useState, useEffect, Fragment } from 'react';
import { getEvolutionName } from './data/evolutions';
import { XMLParser } from 'fast-xml-parser';
import { 
  XMarkIcon, 
  SunIcon, 
  MoonIcon, 
  ListBulletIcon,
  Squares2X2Icon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline';
import { SearchableSelect } from './components/SearchableSelect';

interface ClassInfo {
  classEvo: number;
  className: string;
}

// Componente para renderizar un icono individual desde archivos SVG
const ItemIcon = ({ iconName, className = "" }: { iconName: string; className?: string }) => {
  return (
    <img 
      src={`/_assets/icons/${iconName}.svg`} 
      className={`h-6 w-6 ${className}`}
      alt=""
      style={{ filter: 'brightness(0) invert(1)' }} // Hacer los iconos blancos
    />
  );
};

// Función para obtener el icono correspondiente a cada categoría
const getCategoryIcon = (category: string) => {
  // Mapeo de categorías a nombres de archivos de iconos
  const iconMap: Record<string, string> = {
    'helmets': 'icon_93',     // Casco
    'armors': 'icon_94',      // Armadura
    'pants': 'icon_95',       // Pantalones
    'gloves': 'icon_157',     // Guantes
    'boots': 'icon_158',      // Botas
    // Iconos para el resto de categorías
    'swords': 'icon_161',
    'axes': 'icon_164',
    'maces': 'icon_162',      // Mazas
    'maces and scepters': 'icon_162', // Mazas y cetros
    'spears': 'icon_159',
    'shields': 'icon_90',
    'bows and crossbows': 'icon_163',
    'jewel and misc': 'icon_167',
    'pets and rings and misc': 'icon_170',
    'staffs': 'icon_160',
    'wings': 'icon_166',
    'wings and orbs and spheres': 'icon_166',
    'accessories': 'icon_167',
    'potions': 'icon_168',
    'scrolls': 'icon_171',
    'gems': 'icon_170'
  };

  // Obtener el nombre del icono o usar uno predeterminado
  const iconName = iconMap[category.toLowerCase()] || 'icon_69';
  
  return <ItemIcon iconName={iconName} />
};

interface GameItem {
  Item: string;
  ItemCatID: number;
  ItemCat: string;
  MapID: number;
  MapDrop: string;
  MonsterIndex: number;
  Monster: string;
  isActive: boolean;
  allowbyClass: ClassInfo[];
  ModelFile: string;
  ItemIndex: string;
  Exc: boolean;
  SocketCount: number;
  Rate: string;
}

// Lista fija de clases base estándar de MU Online
// Orden de clases segun las abreviaturas en server.php: DK, DW, FF, MG, DL, SM, RF, GL, RW, SLA, GC, LW, LM, IK, AC
const baseClassesList = [
  'Dark Knight',     // DK
  'Dark Wizard',     // DW
  'Fairy Elf',       // FF
  'Magic Gladiator', // MG
  'Dark Lord',       // DL
  'Summoner',        // SM
  'Rage Fighter',    // RF
  'Grow Lancer',     // GL
  'Rune Wizard',     // RW
  'Slayer',          // SLA
  'Gun Crusher',     // GC
  'White Wizard',    // LW
  'Lemuria Mage',    // LM
  'Illusion Knight', // IK
  'Alchemist',       // AC
];

function App() {
  const [items, setItems] = useState<GameItem[]>([]);
  const [filteredItems, setFilteredItems] = useState<GameItem[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [selectedMap, setSelectedMap] = useState<string>('');
  const [selectedClass, setSelectedClass] = useState<string>('');
  const [itemListData, setItemListData] = useState<Record<string, Record<string, any>>>({});
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [darkMode, setDarkMode] = useState(true);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [loadingProgress, setLoadingProgress] = useState({ current: 0, total: 0 });
  const itemsPerPage = 24;
  const [sliderIndex, setSliderIndex] = useState(0);
  const visibleCount = 7;
  const maxIndex = Math.max(0, baseClassesList.length - visibleCount);
  const visibleClasses = baseClassesList.slice(sliderIndex, sliderIndex + visibleCount);

  // Función para cargar la lista de mapas desde MapList.xml
  const loadMapList = async (): Promise<{mapNames: Record<string, string>, mapIds: string[]}> => {
    try {
      const response = await fetch('/serverfiles/MapList.xml');
      const xmlText = await response.text();
      
      // Configurar el parser
      const parser = new XMLParser({
        ignoreAttributes: false,
        attributeNamePrefix: "",
        allowBooleanAttributes: true
      });
      
      // Parsear el XML
      const result = parser.parse(xmlText);
      
      // Extraer los mapas
      const mapNamesObj: Record<string, string> = {};
      const mapIds: string[] = [];
      
      // Buscar la estructura correcta en el archivo MapList.xml
      let maps: any[] = [];
      
      if (result.MapList && result.MapList.Map) {
        maps = Array.isArray(result.MapList.Map) 
          ? result.MapList.Map 
          : [result.MapList.Map];
      }
      else if (result.MapList && result.MapList.DefaultMaps && result.MapList.DefaultMaps.Map) {
        maps = Array.isArray(result.MapList.DefaultMaps.Map) 
          ? result.MapList.DefaultMaps.Map 
          : [result.MapList.DefaultMaps.Map];
      }
      
      // Procesar todos los mapas encontrados
      if (maps.length > 0) {
        //console.log('Mapas encontrados en XML:', maps.length);
        //console.log('Muestra de un mapa:', JSON.stringify(maps[0]));
        
        maps.forEach((map: any) => {
          // Intentar diferentes campos de ID (Index, Number o id)
          const mapId = (map.Index !== undefined ? map.Index : 
                       (map.Number !== undefined ? map.Number :
                       (map.id !== undefined ? map.id : null)))?.toString();
          
          // Intentar diferentes campos de nombre (Name o File)
          const mapName = map.Name || map.File || `Map ${mapId}`;
          
          if (mapId !== null) {
            //console.log(`Mapa ID: ${mapId}, Nombre: ${mapName}`);
            mapNamesObj[mapId] = mapName;
            if (!mapIds.includes(mapId)) {
              mapIds.push(mapId);
            }
          }
        });
      }
      
      // Asegurar que tenemos mapas del 0 al 143 como mencionó el usuario
      for (let i = 0; i <= 143; i++) {
        const mapId = i.toString();
        if (!mapIds.includes(mapId)) {
          mapIds.push(mapId);
          if (!mapNamesObj[mapId]) {
            mapNamesObj[mapId] = `Map ${mapId}`;
          }
        }
      }
      
      //console.log(`MapList cargado: ${mapIds.length} mapas encontrados`);
      return { mapNames: mapNamesObj, mapIds };
    } catch (error) {
      console.error('Error al cargar la lista de mapas:', error);
      // Valores predeterminados en caso de error
      const basicMapData: Record<string, string> = {
        '0': 'Lorencia',
        '1': 'Dungeon',
        '2': 'Devias',
        '3': 'Noria',
        '4': 'LostTower',
        '5': 'Stadium',
        '6': 'Atlans',
        '7': 'Tarkan',
        '8': 'Devils',
        '9': 'Icarus',
        '10': 'Blood'
      };
      
      return { mapNames: basicMapData, mapIds: Object.keys(basicMapData) };
    }
  };

  // Función para cargar la lista de monstruos desde MonsterList.xml
  const loadMonsterList = async (): Promise<Record<string, string>> => {
    try {
      const response = await fetch('/serverfiles/MonsterList.xml');
      const xmlText = await response.text();
      
      // Configurar el parser
      const parser = new XMLParser({
        ignoreAttributes: false,
        attributeNamePrefix: "",
        allowBooleanAttributes: true
      });
      
      // Parsear el XML
      const result = parser.parse(xmlText);
      
      // Extraer los datos de monstruos
      const monstersData: Record<string, string> = {};
      
      if (result.MonsterList && result.MonsterList.Monster) {
        const monsters = Array.isArray(result.MonsterList.Monster) 
          ? result.MonsterList.Monster 
          : [result.MonsterList.Monster];
        
        monsters.forEach((monster: any) => {
          if (monster.Index !== undefined && monster.Name !== undefined) {
            monstersData[monster.Index] = monster.Name;
          }
        });
      }
      
      return monstersData;
    } catch (error) {
      console.error('Error al cargar la lista de monstruos:', error);
      return {};
    }
  };
  
  // Función para cargar items desde ItemList.xml - respetando la estructura exacta XML
  const loadItemListData = async () => {
    try {
      const response = await fetch('/serverfiles/ItemList.xml');
      const xmlText = await response.text();
      
      // Configurar el parser XML exactamente para la estructura de ItemList.xml
      const parser = new XMLParser({
        ignoreAttributes: false, 
        attributeNamePrefix: "", // Eliminar prefijo para atributos
        textNodeName: "#text",
        isArray: (tagName) => ['Section', 'Item'].includes(tagName)
      });
      
      // Parsear el XML
      const result = parser.parse(xmlText);
      
      // Mapa para acceder fácilmente a cada item por su clave Cat.Index
      const itemsMap: Record<string, Record<string, any>> = {};
      
      // Mapa para almacenar nombres de categorías
      const categories: Record<string, string> = {};
      
      // Procesar la estructura exacta del XML:
      // <ItemList>
      //   <Section Index="0" Name="Swords">
      //     <Item Index="0" Name="Kris" ... DarkWizard="1" ... />
      //   </Section>
      // </ItemList>
      
      // Verificar que exista la estructura correcta
      if (result && result.ItemList && result.ItemList.Section) {
        // Convertir Section a array si es un solo objeto
        const sections = Array.isArray(result.ItemList.Section) 
          ? result.ItemList.Section 
          : [result.ItemList.Section];
        
        //console.log(`Procesando ${sections.length} secciones (categorías) en ItemList.xml`);
        
        // Procesar cada sección (categoría)
        sections.forEach((section: any, sectionIdx: number) => {
          if (!section || typeof section !== 'object') {
            console.warn(`Sección ${sectionIdx} es inválida:`, section);
            return;
          }
          
          // Extraer Index y Name de la sección
          const sectionIndex = section.Index !== undefined ? section.Index.toString() : sectionIdx.toString();
          const sectionName = section.Name || `Categoría ${sectionIndex}`;
          
          // Guardar el nombre de la categoría
          categories[sectionIndex] = sectionName;
          //console.log(`Categoría ${sectionIndex}: ${sectionName}`);
          
          // Verificar si hay items en esta sección
          if (section.Item) {
            // Convertir a array si es un solo item
            const items = Array.isArray(section.Item) ? section.Item : [section.Item];
            
            //console.log(`Sección ${sectionName} contiene ${items.length} items`);
            
            // Procesar cada item en esta sección
            items.forEach((item: any, itemIdx: number) => {
              if (!item || typeof item !== 'object') {
                console.warn(`Item ${itemIdx} en sección ${sectionName} es inválido:`, item);
                return;
              }
              
              // Extraer el Index del item
              const itemIndex = item.Index !== undefined ? item.Index.toString() : itemIdx.toString();
              
              // Clave para el mapa: "Cat.Index" como en "0.0" para Kris
              const key = `${sectionIndex}.${itemIndex}`;
              
              // Guardar el item completo en el mapa, incluyendo info de la sección
              itemsMap[key] = {
                ...item,
                SectionIndex: sectionIndex,
                SectionName: sectionName
              };
              
              // Verificar atributos de clase y mostrar para algunos items
              if (itemIdx < 5) {
                // Lista de posibles atributos de clase
                const classAttributes = [
                  'DarkWizard', 'DarkKnight', 'FairyElf', 'MagicGladiator', 'DarkLord',
                  'Summoner', 'RageFighter', 'GrowLancer', 'RuneWizard', 'Slayer', 
                  'GunCrusher', 'LightWizard', 'LemuriaMage', 'IllusionKnight', 'Alchemist'
                ];
                
                // Filtrar solo los atributos de clase que están definidos
                const presentClassAttrs = classAttributes
                  .filter(attr => item[attr] !== undefined)
                  .map(attr => `${attr}=${item[attr]}`);
                
                if (presentClassAttrs.length > 0) {
                  //console.log(`Item ${item.Name || key} permite clases:`, presentClassAttrs.join(', '));
                }
              }
            });
          } else {
            console.warn(`Sección ${sectionName} no contiene items`);
          }
        });
      } else {
        console.error('Estructura XML no reconocida en ItemList.xml. Se esperaba <ItemList><Section><Item>...</Item></Section></ItemList>');
      }
      
      // Guardar categorías en el estado
      setCategoryMap(categories);
      
      // Guardar en el estado y retornar
      setItemListData(itemsMap);
      return itemsMap;
    } catch (error) {
      console.error('Error al cargar la lista de items:', error);
      return {};
    }
  };
  
  const predefinedCategories: Record<string, string> = {
    "0": "swords",
    "1": "axes",
    "2": "maces and scepters",
    "3": "spears",
    "4": "bows and crossbows",
    "5": "staffs",
    "6": "shields",
    "7": "helmets",
    "8": "armors",
    "9": "pants",
    "10": "gloves",
    "11": "boots",
    "12": "pets and rings and misc",
    "13": "jewel and misc",
    "14": "wings and orbs and spheres",
    "15": "scrolls",
    "16": "muuns",
    "17": "uncategorized",
    "18": "uncategorized",
    "19": "uncategorized",
    "20": "uncategorized"
  };

  // Mapa para almacenar las categorías
  const [categoryMap, setCategoryMap] = useState<Record<string, string>>(predefinedCategories);
  
  // Función para obtener la categoría de un item directamente desde el mapa predefinido
  const getItemCategory = (cat: number): string => {
    // Buscar en el mapa de categorías predefinidas
    const key = cat.toString();
    if (predefinedCategories[key]) {
      return predefinedCategories[key];
    }
    
    // Si no se encuentra, retornar un valor por defecto
    return 'misc';
  };
  
  // Lista fija de nombres de mapas directamente del cliente
  const predefinedMaps: Record<string, string> = {
    "0": "Lorencia",
    "1": "Dungeon",
    "2": "Devias",
    "3": "Noria",
    "4": "Losttower",
    "5": "Null.att",
    "6": "Arena",
    "7": "Atlans",
    "8": "Tarkan",
    "9": "DevilSquare",
    "10": "Icarus",
    "11": "Blood Castle 1",
    "12": "Blood Castle 2",
    "13": "Blood Castle 3",
    "14": "Blood Castle 4",
    "15": "Blood Castle 5",
    "16": "Blood Castle 6",
    "17": "Blood Castle 7",
    "18": "Chaos Castle 1",
    "19": "Chaos Castle 2",
    "20": "Chaos Castle 3",
    "21": "Chaos Castle 4",
    "22": "Chaos Castle 5",
    "23": "Chaos Castle 6",
    "24": "Kalima 1",
    "25": "Kalima 2",
    "26": "Kalima 3",
    "27": "Kalima 4",
    "28": "Kalima 5",
    "29": "Kalima 6",
    "30": "Valley Of Loren",
    "31": "Land Of Trial",
    "32": "Devil Square",
    "33": "Aida",
    "34": "Crywolf",
    "35": "Reserved",
    "36": "Kalima",
    "37": "Kanturu",
    "38": "Kanturu Remain",
    "39": "Kanturu Boss",
    "40": "Silent",
    "41": "Balgass Barracks",
    "42": "Balgass Refuge",
    "43": "Null.att",
    "44": "Null.att",
    "45": "Illusion Temple 1",
    "46": "Illusion Temple 2",
    "47": "Illusion Temple 3",
    "48": "Illusion Temple 4",
    "49": "Illusion Temple 5",
    "50": "Illusion Temple 6",
    "51": "Elbeland",
    "52": "BloodCastle",
    "53": "ChaosCastle",
    "54": "Null.att",
    "55": "Null.att",
    "56": "Swamp Of Calmness",
    "57": "Raklion",
    "58": "Raklion Boss",
    "59": "Null.att",
    "60": "Null.att",
    "61": "Null.att",
    "62": "Santa Town",
    "63": "Vulcanus",
    "64": "Duel Arena",
    "65": "Doppelganger 1",
    "66": "Doppelganger 2",
    "67": "Doppelganger 3",
    "68": "Doppelganger 4",
    "69": "Imperial Guardian 1",
    "70": "Imperial Guardian 2",
    "71": "Imperial Guardian 3",
    "72": "Imperial Guardian 4",
    "73": "Null.att",
    "74": "Null.att",
    "75": "Null.att",
    "76": "Null.att",
    "77": "Null.att",
    "78": "Null.att",
    "79": "LorenMarket EventSquare",
    "80": "Karutan1",
    "81": "Karutan2",
    "82": "Doppelganger Renewal",
    "83": "Doppelganger Renewal",
    "84": "Doppelganger Renewal",
    "85": "Doppelganger Renewal",
    "86": "Doppelganger Renewal",
    "87": "Doppelganger Renewal",
    "88": "Doppelganger Renewal",
    "89": "Doppelganger Renewal",
    "90": "Doppelganger Renewal",
    "91": "Acheron",
    "92": "Acheron",
    "93": "Null.att",
    "94": "Null.att",
    "95": "Debenter",
    "96": "Debenter",
    "97": "ChaosCastle",
    "98": "Illussion Temple League",
    "99": "Illussion Temple League",
    "100": "Urk Montain",
    "101": "Urk Montain",
    "102": "Tormented Square",
    "103": "Tormented Square",
    "104": "Tormented Square",
    "105": "Tormented Square",
    "106": "Tormented Square",
    "107": "Null.att",
    "108": "Null.att",
    "109": "Null.att",
    "110": "Nars",
    "111": "Null.att",
    "112": "Ferea",
    "113": "Nixie Lake",
    "114": "QuestZoneEntrance",
    "115": "Labyrinth",
    "116": "Deep Dungeon 1",
    "117": "Deep Dungeon 2",
    "118": "Deep Dungeon 3",
    "119": "Deep Dungeon 4",
    "120": "Deep Dungeon 5",
    "121": "4thQuest",
    "122": "Swamp Of Darkness",
    "123": "Kubera Mine",
    "124": "Kubera Mine",
    "125": "Kubera Mine",
    "126": "Kubera Mine",
    "127": "Kubera Mine",
    "128": "Abyss Of Atlans 1",
    "129": "Abyss Of Atlans 2",
    "130": "Abyss Of Atlans 3",
    "131": "Scorched Canyon",
    "132": "RedSmoke Icarus",
    "133": "Arenil Temple",
    "134": "Ashy Aida",
    "135": "Old Kethotum",
    "136": "Blaze Kethotum",
    "137": "Kanturu Undergrounds",
    "138": "Volcano Ignis",
    "139": "BossBattle",
    "140": "Bloody Tarkan",
    "141": "Tormenta Island",
    "142": "Twisted Karutan",
    "143": "Kardamahal Underground Temple"
  };

  // Esta función ya no es necesaria pero la mantenemos por compatibilidad
  const loadCategories = (itemListXml: any) => {
    // Usar directamente las categorías predefinidas
    //console.log('Usando categorías predefinidas:', predefinedCategories);
    setCategoryMap(predefinedCategories);
  };
  
  // Función para obtener el nombre de evolución de una clase
  const getClassEvolutionName = (className: string, evolutionValue: number): string => {
    // Si no hay evolución, retornar el nombre normal
    if (evolutionValue <= 0) return className;
    
    // Nombres para cada evolución por clase
    switch(className) {
      case "DarkWizard":
        return ["Dark Wizard", "Soul Master", "Grand Master", "Sould Wizard", "Darkness Wizard"][evolutionValue-1];
      case "DarkKnight":
        return ["Dark Knight", "Blade Knight", "Blade Master", "Blade Knight", "Blade Master"][evolutionValue-1];
      case "FairyElf":
        return ["Fairy Elf", "Muse Elf", "High Elf", "Noble Elf", "Lady Elf"][evolutionValue-1];
      case "MagicGladiator":
        return ["Magic Gladiator", "Duel Master", "Mystic Knight", "Magic Knight", "Magic Swordsman"][evolutionValue-1];
      case "DarkLord":
        return ["Dark Lord", "Lord Emperor", "Empire Lord", "Supreme Lord", "Absolute Lord"][evolutionValue-1];
      case "Summoner":
        return ["Summoner", "Dimension Master", "Dimension Summoner", "Mystic Emperor", "Mystic Emperor"][evolutionValue-1];
      case "RageFighter":
        return ["Rage Fighter", "Fist Master", "Fitness Master", "Ultimate Fighter", "Fist Blazer"][evolutionValue-1];
      case "GrowLancer":
        return ["Grow Lancer", "Mirage Lancer", "Shining Lancer", "Miracle Lancer", "Glow Lancer"][evolutionValue-1];
      case "RuneWizard":
        return ["Rune Wizard", "Rune Spell Master", "Grand Rune Master", "Arch Rune Wizard", "Rune Spectrum Master"][evolutionValue-1];
      case "Slayer":
        return ["Slayer", "Royal Slayer", "Master Slayer", "Slaughterer", "Doom Slayer"][evolutionValue-1];
      case "GunCrusher":
        return ["Gun Crusher", "Gun Breaker", "Master Gun Breaker", "Heist Gun Crusher", "Heist Gun Breaker"][evolutionValue-1];
      case "LightWizard":
        return ["White Wizard", "Light Master", "Light Wizard", "Light Emperor", "Divine Light Emperor"][evolutionValue-1];
      case "LemuriaMage":
        return ["Lemuria Mage", "Warmage", "Archmage", "Archon Mage", "Grand Mage"][evolutionValue-1];
      case "IllusionKnight":
        return ["Illusion Knight", "Mirage Knight", "Illusion Master", "Mystic Knight", "Phantom Pain Knight"][evolutionValue-1];
      case "Alchemist":
        return ["Alchemist", "Alchemic Magician", "Alchemic Master", "Alchemic Force", "Creator"][evolutionValue-1];
      default:
        return className;
    }
  };
  
  // Función para convertir nombre de clase UI a nombre en XML
  const getClassXmlName = (className: string): string => {
    // Mapa de nombres de clase a nombres de atributos XML
    const classMapping: Record<string, string> = {
      "Dark Wizard": "DarkWizard",
      "Soul Master": "DarkWizard",
      "Grand Master": "DarkWizard",
      "Dark Knight": "DarkKnight",
      "Blade Knight": "DarkKnight",
      "Blade Master": "DarkKnight",
      "Fairy Elf": "FairyElf",
      "Magic Gladiator": "MagicGladiator",
      "Dark Lord": "DarkLord",
      "Summoner": "Summoner",
      "Rage Fighter": "RageFighter",
      "Grow Lancer": "GrowLancer",
      "Rune Wizard": "RuneWizard",
      "Slayer": "Slayer",
      "Gun Crusher": "GunCrusher",
      "White Wizard": "LightWizard",
      "Lemuria Mage": "LemuriaMage",
      "Illusion Knight": "IllusionKnight",
      "Alchemist": "Alchemist",
    };
    
    return classMapping[className] || "";
  };
  
  // Función para verificar si una clase puede usar un item según los atributos en XML
  const canClassUseItem = (className: string, itemData: any): boolean => {
    if (!itemData) return false;
    
    // Mapeo directo de nombres de clase a atributos XML (sin función intermedia para evitar errores)
    const classToXmlAttr: Record<string, string> = {
      "Dark Wizard": "DarkWizard",
      "Dark Knight": "DarkKnight",
      "Fairy Elf": "FairyElf",
      "Magic Gladiator": "MagicGladiator",
      "Dark Lord": "DarkLord",
      "Summoner": "Summoner",
      "Rage Fighter": "RageFighter",
      "Grow Lancer": "GrowLancer",
      "Rune Wizard": "RuneWizard",
      "Slayer": "Slayer",
      "Gun Crusher": "GunCrusher",
      "White Wizard": "LightWizard",
      "Lemuria Mage": "LemuriaMage",
      "Illusion Knight": "IllusionKnight",
      "Alchemist": "Alchemist"
    };
    
    // Obtener el nombre del atributo XML exacto
    const xmlAttr = classToXmlAttr[className];
    if (!xmlAttr) {
      console.error(`No se encontró atributo XML para clase: ${className}`);
      return false;
    }
    
    // DIAGNOSTICO: Ver todos los atributos disponibles
    console.log('Atributos disponibles:', Object.keys(itemData));
    
    // IMPORTANTE: Verificar con insensibilidad a mayúsculas/minúsculas
    // Buscar el atributo independientemente de las mayúsculas/minúsculas
    let foundAttr = null;
    let foundValue = null;
    
    for (const key in itemData) {
      if (key.toLowerCase() === xmlAttr.toLowerCase()) {
        foundAttr = key;
        foundValue = itemData[key];
        break;
      }
    }
    
    if (!foundAttr) {
      // Buscar cualquier atributo que comience con el nombre de la clase
      for (const key in itemData) {
        if (key.toLowerCase().startsWith(xmlAttr.toLowerCase().substring(0, 4))) {
          console.log(`Encontrado atributo similar: ${key} para clase ${className}`);
          foundAttr = key;
          foundValue = itemData[key];
          break;
        }
      }
    }
    
    if (!foundAttr) {
      return false; // No se encontró el atributo ni nada similar
    }
    
    // Convertir el valor encontrado a número
    let attrValue;
    if (typeof foundValue === 'string') {
      attrValue = parseInt(foundValue);
    } else if (typeof foundValue === 'number') {
      attrValue = foundValue;
    } else {
      attrValue = 0;
    }
    
    // Debe ser estrictamente mayor que 0
    return attrValue > 0;
  };
  
  // Función para cargar items desde los archivos XML de MonsterMap_Bag
  const loadMonsterMapItems = async (mapIds: string[], mapNames: Record<string, string>): Promise<GameItem[]> => {
    const allItems: GameItem[] = [];
    
    try {
      // Resetear el progreso de carga
      setLoadingProgress({ current: 0, total: mapIds.length });
      
      // Cargar datos necesarios
      const monsters = await loadMonsterList();
      
      // Cargar datos de los items - ahora esto pasa por setItemListData en useEffect
      const itemsData = itemListData;
      //const classesData = await loadEvolutionClasses();
      
      // Procesar cada mapa
      for (const mapId of mapIds) {
        try {
          const url = `/serverfiles/MonsterMap_Bag/MonsterMap_(${mapId})_Bag.xml`;
          const response = await fetch(url);
          
          if (!response.ok) {
            continue; // Saltar este mapa si no existe el archivo
          }
          
          const xmlText = await response.text();
          
          // Configurar el parser
          const parser = new XMLParser({
            ignoreAttributes: false,
            attributeNamePrefix: "",
            allowBooleanAttributes: true
          });
          
          // Parsear el XML
          const result = parser.parse(xmlText);
          
          // Actualizar progreso - mapas procesados
          setLoadingProgress(prev => ({ 
            current: prev.current + 1, 
            total: mapIds.length 
          }));
          
          // El formato puede variar, intentamos todas las estructuras posibles
          let dropItems: any[] = [];
          
          // ESTRUCTURA DETECTADA EN LA CONSOLA: MonsterMapItemDrop.DropItem
          if (result.MonsterMapItemDrop && result.MonsterMapItemDrop.DropItem) {
            //console.log(`Mapa ${mapId}: Estructura de DropItem en MonsterMapItemDrop`);
            const items = Array.isArray(result.MonsterMapItemDrop.DropItem) 
              ? result.MonsterMapItemDrop.DropItem 
              : [result.MonsterMapItemDrop.DropItem];
            dropItems = [...items];
          }
          // Comprobar si es un objeto simple con atributos DropItem
          else if (result && result.DropItem) {
            //console.log(`Mapa ${mapId}: Estructura de DropItem directo en la raíz`);
            const items = Array.isArray(result.DropItem) 
              ? result.DropItem 
              : [result.DropItem];
            dropItems = [...items];
          }
          // Estructura 1: DropItem directo en ItemBag
          else if (result.ItemBag && result.ItemBag.DropItem) {
            //console.log(`Mapa ${mapId}: Estructura de DropItem en ItemBag`);
            const items = Array.isArray(result.ItemBag.DropItem) 
              ? result.ItemBag.DropItem 
              : [result.ItemBag.DropItem];
            dropItems = [...items];
          }
          // Estructura 2: DropItem dentro de secciones
          else if (result.ItemBag) {
            // Buscar en todas las propiedades de ItemBag por si hay diferentes secciones
            Object.keys(result.ItemBag).forEach(key => {
              const section = result.ItemBag[key];
              if (section && section.DropItem) {
                const items = Array.isArray(section.DropItem) 
                  ? section.DropItem 
                  : [section.DropItem];
                dropItems.push(...items);
              }
            });
          }
          
          // Actualizar el progreso de carga
          setLoadingProgress(prev => ({
            current: prev.current + 1,
            total: prev.total
          }))
          
          // Si no hay items, continuar con el siguiente mapa
          if (dropItems.length === 0) {
            continue;
          }
          
          // Procesar cada item
          for (const dropItem of dropItems) {
            const monsterId = dropItem.MonsterID || '0';
            const cat = parseInt(dropItem.Cat || '0');
            const index = parseInt(dropItem.Index || '0');
            const rate = parseFloat(dropItem.Rate || '0'); // Usar el valor original del Rate
            
            // Verificar si es Excellent - según la explicación, Exc=-2 significa que es Excellent
            const exc = dropItem.Exc === '-2';
            
            // Procesar SocketCount - verificar si NO es "0;0"
            let socketCount = 0;
            if (dropItem.SocketCount && dropItem.SocketCount !== '0;0') {
              socketCount = 1; // Item tiene socket
            }
            
            // Obtener el nombre del monstruo
            const monsterName = monsters[monsterId] || `Monster ${monsterId}`;
            
            // Obtener datos del item desde ItemList.xml
            const catStr = cat.toString();
            const indexStr = index.toString();
            
            // Intentar obtener el item de manera directa y eficiente
            let itemData = null;
            if (itemsData[catStr] && itemsData[catStr][indexStr]) {
              itemData = itemsData[catStr][indexStr];
            }
            
            // Sin mostrar logs para mejorar rendimiento
            
            // Determinar el nombre del item
            const itemName = (dropItem.Name || (itemData && itemData.Name)) || `Item ${cat}.${index}`;
            
            // Determinar la categoría del item directamente usando el índice de categoría (cat)
            // que coincide con el Index de Section en el XML
            const itemCategory = getItemCategory(cat);
            
            // Log para debugging (solo para algunos items)
            //if (dropItems.indexOf(dropItem) < 5) {
            //  console.log(`Item ${itemName} [${cat}.${index}]: asignado a categoría "${itemCategory}"`);
            //}
            
            // Determinar clases permitidas - MÉTODO DIRECTO para encontrar todas las clases
            let allowbyClass: ClassInfo[] = [];
            
            // NUEVO ENFOQUE: verificar directamente todos los atributos en busca de valores > 0
            if (itemData) {
              // Lista de todas las clases posibles
              const clasesPosibles = [
                {xmlName: "DarkWizard", uiName: "Dark Wizard"},
                {xmlName: "DarkKnight", uiName: "Dark Knight"},
                {xmlName: "FairyElf", uiName: "Fairy Elf"},
                {xmlName: "MagicGladiator", uiName: "Magic Gladiator"},
                {xmlName: "DarkLord", uiName: "Dark Lord"},
                {xmlName: "Summoner", uiName: "Summoner"},
                {xmlName: "RageFighter", uiName: "Rage Fighter"},
                {xmlName: "GrowLancer", uiName: "Grow Lancer"},
                {xmlName: "RuneWizard", uiName: "Rune Wizard"},
                {xmlName: "Slayer", uiName: "Slayer"},
                {xmlName: "GunCrusher", uiName: "Gun Crusher"},
                {xmlName: "LightWizard", uiName: "White Wizard"},
                {xmlName: "LemuriaMage", uiName: "Lemuria Mage"},
                {xmlName: "IllusionKnight", uiName: "Illusion Knight"},
                {xmlName: "Alchemist", uiName: "Alchemist"}
              ];
              
              // Loggear todos los atributos para debugging
              console.log(`Item ${itemName} - Atributos:`, Object.keys(itemData).join(', '));
              
              // Recorrer cada clase posible
              for (const clase of clasesPosibles) {
                // Buscar el atributo para esta clase (insensible a mayúsculas/minúsculas)
                for (const key in itemData) {
                  if (key.toLowerCase() === clase.xmlName.toLowerCase()) {
                    // Intentar convertir a número
                    const valor = parseInt(String(itemData[key]));
                    
                    // Si el valor es > 0, añadir la clase
                    if (valor > 0) {
                      // Añadir esta clase a las permitidas
                      allowbyClass.push({
                        className: clase.uiName,
                        classEvo: 0
                      });
                      
                      // Log para ver lo que encontramos
                      console.log(`Item ${itemName} PUEDE ser equipado por ${clase.uiName} (${key}=${valor})`);
                    }
                  }
                }
              }
              
              // USAR EXACTAMENTE LOS ATRIBUTOS DEL XML PARA DETERMINAR LAS CLASES PERMITIDAS
              // Si un ítem tiene el atributo DarkWizard="1", entonces Dark Wizard puede usarlo
              
              // Limpiar el array de clases permitidas para empezar de nuevo
              allowbyClass = [];
              
              // Lista de todas las clases posibles con sus nombres en XML y UI
              const clasesDisponibles = [
                {xmlName: "DarkWizard", uiName: "Dark Wizard"},
                {xmlName: "DarkKnight", uiName: "Dark Knight"},
                {xmlName: "FairyElf", uiName: "Fairy Elf"},
                {xmlName: "MagicGladiator", uiName: "Magic Gladiator"},
                {xmlName: "DarkLord", uiName: "Dark Lord"},
                {xmlName: "Summoner", uiName: "Summoner"},
                {xmlName: "RageFighter", uiName: "Rage Fighter"},
                {xmlName: "GrowLancer", uiName: "Grow Lancer"},
                {xmlName: "RuneWizard", uiName: "Rune Wizard"},
                {xmlName: "Slayer", uiName: "Slayer"},
                {xmlName: "GunCrusher", uiName: "Gun Crusher"},
                {xmlName: "LightWizard", uiName: "White Wizard"},
                {xmlName: "LemuriaMage", uiName: "Lemuria Mage"},
                {xmlName: "IllusionKnight", uiName: "Illusion Knight"},
                {xmlName: "Alchemist", uiName: "Alchemist"}
              ];
              
              // Comprobar cada clase usando la misma lógica que en canClassUseItem()
              if (itemData) {
                for (const clase of clasesDisponibles) {
                  // Probar con diferentes variantes del nombre del atributo
                  const variantes = [
                    clase.xmlName,
                    clase.xmlName.toLowerCase(),
                    clase.xmlName.toUpperCase()
                  ];
                  
                  for (const variante of variantes) {
                    for (const key in itemData) {
                      if (key.toLowerCase() === variante.toLowerCase()) {
                        // Convertir el valor a número
                        const valor = parseInt(String(itemData[key]));
                        
                        // Si el valor es mayor que 0, esta clase puede usar el ítem
                        if (valor > 0) {
                          allowbyClass.push({
                            className: clase.uiName,
                            classEvo: 0
                          });
                          
                          // Sin logs para mejorar rendimiento
                          break; // Salir del bucle de keys una vez encontrado
                        }
                      }
                    }
                  }
                }
              }
              
              // Se eliminan todos los logs para mejorar el rendimiento
            }
            
            // Obtener el ModelPath y ModelFile exactamente como aparecen en el XML
            const modelPath = itemData && itemData.ModelPath ? itemData.ModelPath : '';
            const modelFileName = itemData && itemData.ModelFile ? itemData.ModelFile : '';
            
            // Combinar la ruta completa usando exactamente los valores del XML
            const modelFile = (modelPath && modelFileName) ? 
              `${modelPath}${modelFileName}` : 
              `Item_${cat}_${index}.bmd`; // Solo como respaldo si no hay datos
            
            // Log detallado para ver el allowbyClass antes de crear el GameItem
            // console.log(`Item ${itemName} - allowbyClass:`, JSON.stringify(allowbyClass));
            
            // Crear una copia del array para asegurarnos de que no se modifique
            const allowbyClassCopy = [...allowbyClass];
            
            // Crear el objeto GameItem
            const gameItem: GameItem = {
              Item: itemName,
              ItemCatID: cat,
              ItemCat: itemCategory,
              MapID: parseInt(mapId),
              // Usar nuestro listado predefinido de mapas para mayor consistencia
              MapDrop: predefinedMaps[mapId] || mapNames[mapId] || `Map ${mapId}`,
              MonsterIndex: parseInt(monsterId),
              Monster: monsterName,
              isActive: true,
              allowbyClass: allowbyClassCopy, // Usamos la copia del array
              ModelFile: modelFile,
              ItemIndex: index.toString(), // Convertir a string para resolver el error de tipo
              Exc: exc,
              SocketCount: socketCount,
              Rate: rate.toString()
            };
            
            allItems.push(gameItem);
          }
        } catch (mapError) {
          console.error(`Error procesando el mapa ${mapId}:`, mapError);
        }
      }
      
      // console.log(`Total de items cargados: ${allItems.length}`);
      return allItems;
    } catch (error) {
      console.error('Error general al cargar items:', error);
      return [];
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Primero cargar los datos de ItemList.xml
        await loadItemListData(); // Esta función ya llama a setItemListData
        
        // Cargar la lista de mapas
        const mapData = await loadMapList();
        
        // Luego cargar los items de los mapas
        const loadedItems = await loadMonsterMapItems(mapData.mapIds, mapData.mapNames);
        
        setItems(loadedItems);
        setFilteredItems(loadedItems);
        setLoading(false);
      } catch (error) {
        console.error('Error al cargar datos desde archivos XML:', error);
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [darkMode]);
  
  // Activar modo oscuro por defecto al cargar la aplicación
  useEffect(() => {
    document.documentElement.classList.add('dark');
  }, []);


  // Función para aplicar todos los filtros a los items
  const applyFilters = (search: string, category: string | null, map: string | null, cls: string | null, itemList: GameItem[]): GameItem[] => {
    let result = [...itemList]; // Crear una copia para no modificar el original
    
    // Inicialmente, mostrar todos los items - sin logs para mejorar rendimiento
    
    // Aplicar filtro de búsqueda
    if (search) {
      const searchLower = search.toLowerCase();
      result = result.filter(item => 
        item.Item.toLowerCase().includes(searchLower) ||
        item.Monster.toLowerCase().includes(searchLower) ||
        item.MapDrop.toLowerCase().includes(searchLower)
      );
      
      // Sin logs para mejorar rendimiento
    }
    
    // Aplicar filtro de categoría
    if (category && category !== 'all') {
      result = result.filter(item => item.ItemCat.toLowerCase() === category.toLowerCase());
      // Sin logs para mejorar rendimiento
    }
    
    // Aplicar filtro de mapa - CORREGIDO para usar MapDrop (nombre del mapa) en lugar de MapID
    if (map && map !== 'all') {
      // Sin logs para mejorar rendimiento
      
      // Filtrar por el NOMBRE del mapa (MapDrop), no por el ID
      result = result.filter(item => {
        // Comparar el nombre del mapa de forma insensible a mayúsculas/minúsculas
        const itemMapName = item.MapDrop?.toLowerCase() || '';
        const targetMapName = map.toLowerCase();
        
        // Verificar si coincide el nombre del mapa
        const matches = itemMapName === targetMapName;
        
        return matches;
      });
      
      // Sin logs para mejorar rendimiento
    }
    
    // Aplicar filtro de clase
    if (cls && cls !== 'all') {
      // Sin logs para mejorar rendimiento
      
      // Filtrar por clase de manera más flexible
      result = result.filter(item => {
        // Obtener los datos del item desde itemListData
        const cat = item.ItemCatID;
        const index = item.ItemIndex;
        const key = `${cat}.${index}`;
        
        // Intentar diferentes formatos de clave
        const keys = [
          `${cat}.${index}`,   // Formato principal
          `${cat}-${index}`,   // Formato alternativo
          `${cat}_${index}`    // Otro formato posible
        ];
        
        // Buscar en todos los formatos posibles
        for (const k of keys) {
          if (itemListData[k]) {
            const itemXmlData = itemListData[k];
            
            // IMPORTANTE: Buscar claves de forma insensible a mayúsculas/minúsculas
            const mapeo: Record<string, string[]> = {
              "Dark Wizard": ["DarkWizard", "darkwizard", "DARKWIZARD"],
              "Dark Knight": ["DarkKnight", "darkknight", "DARKKNIGHT"],
              "Fairy Elf": ["FairyElf", "fairyelf", "FAIRYELF"],
              "Magic Gladiator": ["MagicGladiator", "magicgladiator", "MAGICGLADIATOR"],
              "Dark Lord": ["DarkLord", "darklord", "DARKLORD"],
              "Summoner": ["Summoner", "summoner", "SUMMONER"],
              "Rage Fighter": ["RageFighter", "ragefighter", "RAGEFIGHTER"],
              "Grow Lancer": ["GrowLancer", "growlancer", "GROWLANCER"],
              "Rune Wizard": ["RuneWizard", "runewizard", "RUNEWIZARD"],
              "Slayer": ["Slayer", "slayer", "SLAYER"],
              "Gun Crusher": ["GunCrusher", "guncrusher", "GUNCRUSHER"],
              "White Wizard": ["LightWizard", "lightwizard", "LIGHTWIZARD"],
              "Lemuria Mage": ["LemuriaMage", "lemuriamage", "LEMURIAMAGE"],
              "Illusion Knight": ["IllusionKnight", "illusionknight", "ILLUSIONKNIGHT"],
              "Alchemist": ["Alchemist", "alchemist", "ALCHEMIST"]
            };
            
            // Buscar en todas las variantes del nombre de la clase
            // Uso una variable temporal para evitar errores de tipo
            let claseSeleccionada = cls || '';
            const posiblesAtributos = mapeo[claseSeleccionada] || [];
            
            for (const attrName of posiblesAtributos) {
              // Buscar el atributo de forma insensible a mayúsculas/minúsculas
              for (const xmlKey in itemXmlData) {
                if (xmlKey.toLowerCase() === attrName.toLowerCase()) {
                  const valor = parseInt(itemXmlData[xmlKey]);
                  if (valor > 0) {
                    return true; // Este item puede ser usado por la clase
                  }
                }
              }
            }
          }
        }
        
        // Si llegamos aquí, el item no puede ser usado por la clase seleccionada
        return false;
      });
      
      // Sin logs para mejorar rendimiento
    }
    
    return result;
  };

  // Efecto para aplicar filtros cuando cambian
  useEffect(() => {
    // Aplicar filtros y actualizar el estado directamente
    const filteredResults = applyFilters(searchTerm, selectedCategory, selectedMap, selectedClass, items);
    setFilteredItems(filteredResults);
  }, [searchTerm, selectedCategory, selectedMap, selectedClass, items, itemListData]);

  const resetFilters = () => {
    setSearchTerm('');
    setSelectedCategory('');
    setSelectedMap('');
    setSelectedClass('');
  };

  const categories = Array.from(new Set(items.map(item => item.ItemCat))).sort();
  const maps = Array.from(new Set(items.map(item => item.MapDrop))).sort();
  // En lugar de extraer y ordenar las clases de los items, usamos el orden predefinido en baseClassesList
  // Esto garantiza que el orden visual coincida con el orden deseado (DK primero, AC último)
  const classes = baseClassesList;

  // Obtener clases base (sin evoluciones)
  const baseClasses = Array.from(new Set(classes.map(cls => cls.split(' (')[0])));

  const startIndex = (page - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentItems = filteredItems.slice(startIndex, endIndex);
  const totalPages = Math.ceil(filteredItems.length / itemsPerPage);

  const handlePageChange = (value: number) => {
    setPage(value);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Efecto para aplicar/quitar la clase dark al elemento HTML según el estado darkMode
  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
      document.body.classList.add('dark');
      document.body.classList.remove('light');
    } else {
      document.documentElement.classList.remove('dark');
      document.body.classList.remove('dark');
      document.body.classList.add('light');
    }
  }, [darkMode]);

  // No renderizamos un loading para toda la pantalla, se mostrará solo en la sección de ítems

  return (
    <div className="min-h-screen dark:bg-gray-900">
      <div className="container mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            {/* Aqui colocare el logo  */}
          </h1>
          <div className="flex items-center gap-4">
            <button
              onClick={() => setViewMode(viewMode === 'grid' ? 'list' : 'grid')}
              className="p-2 rounded-lg bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
            >
              {viewMode === 'grid' ? (
                <ListBulletIcon className="h-6 w-6 text-gray-700 dark:text-gray-300" />
              ) : (
                <Squares2X2Icon className="h-6 w-6 text-gray-700 dark:text-gray-300" />
              )}
            </button>
            <a 
              href="/events"
              className="px-4 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-700 transition-colors"
            >
              Eventos
            </a>
            <button
              onClick={() => setDarkMode(!darkMode)}
              className="p-2 rounded-lg bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
            >
              {darkMode ? (
                <SunIcon className="h-6 w-6 text-yellow-500" />
              ) : (
                <MoonIcon className="h-6 w-6 text-gray-700" />
              )}
            </button>
          </div>
        </div>

        <div className="flex flex-col items-center mb-8">
          {/*h1 className="text-5xl font-bold text-white drop-shadow-lg mb-8 mt-8">
            Drop List
          </h1*/}
          <div className="relative w-full flex flex-col items-center justify-center">
            <div
              className="flex flex-row gap-8 mb-2 px-2 justify-center items-center w-full transition-all duration-300"
              style={{ maxWidth: '100vw' }}
            >
              {visibleClasses.map((baseCls) => (
                <button
                  key={baseCls}
                  onClick={() => {
                    // Establecer la clase seleccionada
                    setSelectedClass(baseCls);
                    // Aplicar todos los filtros, incluido el de clase
                    applyFilters(searchTerm, selectedCategory, selectedMap, baseCls, items);
                    setPage(1);
                  }}
                  className={`group flex flex-col items-center focus:outline-none transition-transform duration-300 ${selectedClass && selectedClass.startsWith(baseCls) ? 'scale-110 animate-pulse' : 'opacity-80 hover:scale-105'}`}
                  style={{ minWidth: 100 }}
                >
                  <div className="relative flex flex-col items-center" style={{ width: 80 }}>
                    <div className="relative flex items-center justify-center" style={{ width: 80, height: 80 }}>
                      {/* Imagen del personaje, centrada y contenida */}
                      <img
                        src={`/class/${baseCls.replace(/\s+/g, '')}Image.webp`}
                        alt={baseCls}
                        className={`w-14 h-14 object-contain z-10 rounded-full transition-all duration-200 ${selectedClass && selectedClass.startsWith(baseCls) ? 'ring-2 ring-orange-400 shadow-lg shadow-orange-400/80' : ''}`}
                        style={{ background: 'rgba(0,0,0,0.0)' }}
                      />
                      {/* Aro decorativo, centrado */}
                      <img
                        src="/hero-slider-mini-bg.png"
                        alt="Aro"
                        className="absolute left-0 top-0 w-20 h-20 z-20 pointer-events-none select-none"
                        draggable="false"
                      />
                    </div>
                    <span className={`text-xs font-semibold text-white text-center px-2 py-1 rounded bg-gray-900/80 shadow-md mt-0 truncate w-24 ${selectedClass && selectedClass.startsWith(baseCls) ? 'font-bold' : 'opacity-80 group-hover:opacity-100'}`}>{baseCls}</span>
                  </div>
                </button>
              ))}
            </div>
            {/* Flechas debajo del slider */}
            <div className="flex flex-row gap-8 items-center justify-center mt-6 mb-4">
              <button
                className="flex items-center justify-center h-10 w-10 rounded-full bg-gray-800/80 hover:bg-primary-500/80 transition-colors duration-200 focus:outline-none disabled:opacity-30"
                onClick={() => setSliderIndex(i => Math.max(0, i - 1))}
                disabled={sliderIndex === 0}
                aria-label="Previous"
              >
                <ChevronLeftIcon className="w-6 h-6 text-gray-300 group-hover:text-white" />
              </button>
              <button
                className="flex items-center justify-center h-10 w-10 rounded-full bg-gray-800/80 hover:bg-primary-500/80 transition-colors duration-200 focus:outline-none disabled:opacity-30"
                onClick={() => setSliderIndex(i => Math.min(maxIndex, i + 1))}
                disabled={sliderIndex === maxIndex}
                aria-label="Next"
              >
                <ChevronRightIcon className="w-6 h-6 text-gray-300 group-hover:text-white" />
              </button>
            </div>
          </div>
        </div>

        <div className="sticky top-0 z-[60] rounded-lg bg-gray-900 text-white dark:bg-gray-950 px-4 py-4 my-4 space-y-4">
          <div className="flex flex-col md:flex-row gap-4">
            <input
              type="text"
              placeholder="Items or Monsters"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="input flex-1"
            />

            <SearchableSelect
              options={categories}
              value={selectedCategory}
              onChange={setSelectedCategory}
              placeholder="Select Category"
              label="Category"
            />

            <SearchableSelect
              options={maps}
              value={selectedMap}
              onChange={setSelectedMap}
              placeholder="Select Map"
              label="Map"
            />

            <SearchableSelect
              options={classes}
              value={selectedClass}
              onChange={setSelectedClass}
              placeholder="Select Class"
              label="Class"
            />

            <button
              onClick={resetFilters}
              className="btn btn-secondary flex items-center gap-2"
            >
              <XMarkIcon className="h-5 w-5" />
              Reset
            </button>
          </div>

          {/* Barra de iconos de categorías */}
          <div className="w-full bg-gray-900/90 border border-gray-800 rounded-lg py-2.5 px-3 mb-3">
            <div className="overflow-x-auto no-scrollbar">
              <div className="grid grid-flow-col auto-cols-max gap-3 px-2 py-2 justify-start items-center">
                {/* Elemento no categorizado */}
                <div 
                  onClick={() => setSelectedCategory('')}
                  title="All Items"
                  className={`flex items-center justify-center cursor-pointer transition-all duration-150 hover:scale-105 p-1.5 rounded-full ${selectedCategory === '' ? 'bg-primary-600/50 dark:bg-primary-800/70 ring-1 ring-primary-500' : 'hover:bg-gray-800'}`}
                >
                  <div className="h-9 w-9 flex items-center justify-center bg-gradient-to-b from-gray-800 to-gray-950 rounded-full border border-gray-600 shadow-inner shadow-black">
                    <Squares2X2Icon className="h-6 w-6 text-white drop-shadow-sm" />
                  </div>
                </div>
                
                {/* Mostrar iconos para cada categoría con efecto hover y tooltip */}
                {categories.map((category) => (
                  <div 
                    key={category}
                    onClick={() => setSelectedCategory(selectedCategory === category ? '' : category)}
                    title={category} /* Tooltip que muestra el nombre de la categoría */
                    className={`flex items-center justify-center cursor-pointer transition-all duration-150 hover:scale-105 p-1.5 rounded-full ${selectedCategory === category ? 'bg-primary-600/50 dark:bg-primary-800/70 ring-1 ring-primary-500' : 'hover:bg-gray-800'}`}
                  >
                    <div className="h-9 w-9 flex items-center justify-center bg-gradient-to-b from-gray-800 to-gray-950 rounded-full border border-gray-600 shadow-inner shadow-black">
                      {getCategoryIcon(category)}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Texto informativo */}
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Showing {filteredItems.length} items
          </p>
        </div>

        <div className="rounded-lg bg-gray-900 dark:bg-gray-950 px-4 py-4 min-h-[300px]" style={{marginBottom: '100px'}}>
          {loading ? (
            <div className="flex flex-col justify-center items-center h-[300px]">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-600 mb-4"></div>
              
              {/* Barra de progreso */}
              <div className="w-64 bg-gray-700 rounded-full h-2.5 mb-2 overflow-hidden">
                <div 
                  className="bg-primary-600 h-2.5 rounded-full transition-all duration-300" 
                  style={{ width: `${loadingProgress.total > 0 ? (loadingProgress.current / loadingProgress.total) * 100 : 0}%` }}
                ></div>
              </div>
              
              {/* Texto de progreso */}
              <p className="text-gray-400 dark:text-gray-300 text-sm">
                Loading group of {loadingProgress.current} items from {loadingProgress.total} maps
              </p>
            </div>
          ) : filteredItems.length === 0 ? (
            <div className="flex flex-col justify-center items-center h-[300px] text-center">
              <div className="text-gray-500 dark:text-gray-400 text-lg mb-2">No items found</div>
              <p className="text-gray-400 dark:text-gray-500 text-sm">Try with other search criteria</p>
              <button
                onClick={resetFilters}
                className="mt-4 btn btn-primary flex items-center gap-2"
              >
                <XMarkIcon className="h-5 w-5" />
                Reset Filters
              </button>
            </div>
          ) : (
            <div className={viewMode === 'grid' 
              ? "grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 gap-2"
              : "space-y-3"
            }>
              {/* Eliminar el caracter '0' de la renderización */}
              <style>{`
                /* Solución simple: cubrir solo el "0" con un parche negro */
                .item-container::before {
                  content: "" !important;
                  display: block !important;
                  position: absolute !important;
                  top: 0 !important;
                  left: 0 !important;
                  width: 18px !important;
                  height: 20px !important;
                  background-color: #080b0f !important;
                  z-index: 20 !important;
                  color: #080b0f !important;
                  pointer-events: none !important;
                  border-bottom-right-radius: 5px !important;
                }
              
              `}</style>
              {currentItems.map((item, index) => (
                <div
                  key={`item-${index}`}
                  className={`bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden ${
                    viewMode === 'list' ? 'flex' : ''
                  }`}
                >
                  <div className={`${viewMode === 'list' ? 'flex gap-4 w-full' : ''} p-2`}>
                    <div className={`${viewMode === 'list' ? 'flex gap-4 w-full' : 'flex flex-col gap-2'}`}>
                      {/* Nuevo contenedor de imagen que evita el 0 usando div con ::after para reemplazar el contenido */}
                      <div 
                        className={`item-container ${viewMode === 'list' ? 'w-24' : 'w-full'} aspect-square bg-black/80 rounded-lg border-2 ${item.Exc ? 'border-green-500' : item.SocketCount && item.SocketCount > 0 ? 'border-purple-500' : 'border-primary-500'} shadow-lg p-1.5 relative overflow-hidden`}
                        data-item-id={`${item.ItemCatID}-${item.ItemIndex}`}
                        data-item-name={item.Item.replace(/\d+$/, '')}
                        // Sobreescribir cualquier texto que pueda estar causando el 0
                        data-item-value=""
                      >
                        {/* Fondo oscuro para combinar con el tema dark */}
                        <div className="absolute inset-0 bg-gray-900/80 opacity-20"></div>
                        
                        {/* Contenedor exclusivo para la imagen con z-index alto */}
                        <div className="absolute inset-0 flex items-center justify-center z-20">
                          <img
                            src={`/items/${item.ItemCatID}-${item.ItemIndex}.gif`}
                            alt={item.Item.replace(/\d+$/, '')}
                            loading="lazy"
                            className="max-w-full max-h-full object-contain"
                            onError={(e) => {
                              const target = e.target as HTMLImageElement;
                              target.src = '/placeholder.png';
                            }}
                          />
                        </div>

                        {/* Badges para ítems especiales, posicionados encima de todo */}
                        <div className="absolute inset-0 pointer-events-none z-30">
                          {/* Badge para Excellent */}
                          {item.Exc && (
                            <div className="absolute top-0 right-0 bg-green-500 text-black text-xs font-bold px-1.5 py-0.5 rounded-bl shadow-md pointer-events-auto">
                              Excellent Item
                            </div>
                          )}
                          
                          {/* Badge para Socket */}
                          {item.SocketCount && item.SocketCount > 0 && (
                            <div className={`absolute ${item.Exc ? 'top-7' : 'top-0'} right-0 bg-purple-500 text-white text-xs font-bold px-1.5 py-0.5 ${item.Exc ? 'rounded-br' : 'rounded-bl'} shadow-md pointer-events-auto`}>
                              Socket Item
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="flex-1">
                        <div className="flex flex-col gap-1.5">
                          <div>
                            <h2 className={`text-base font-semibold line-clamp-1 ${item.Exc ? 'text-green-500 dark:text-green-400' : (item.SocketCount && item.SocketCount > 0) ? 'text-purple-500 dark:text-purple-400' : 'text-gray-900 dark:text-white'}`}>
                              {item.Item.replace(/\d+$/, '')}
                            </h2>
                            <div className="mt-1 flex flex-wrap gap-1">
                              <span className="px-1.5 py-0.5 text-xs rounded-full bg-primary-100 text-primary-800 dark:bg-primary-900 dark:text-primary-100">
                                {item.ItemCat}
                              </span>
                              <span className="px-1.5 py-0.5 text-xs rounded-full bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-100">
                                {item.MapDrop}
                              </span>
                              <span className="px-1.5 py-0.5 text-xs rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-100">
                                {item.Monster}
                              </span>
                            </div>
                          </div>
                          <span className="text-xs text-gray-500 dark:text-gray-400 line-clamp-1">
                            Model: 
                            <span className="mx-1 px-2 py-2 text-xs rounded-full bg-primary-100 text-gray-800 dark:bg-gray-900 dark:text-gray-100">
                              {item.ModelFile}
                            </span>
                          </span>
                        </div>

                        {/* HACK: Añadir clases permitidas directamente aquí para evitar problemas con el renderizado */}
                        {/* Llamamos a console.log pero lo hacemos en un comentario para evitar errores de lint */}
                        {/* {console.log('Renderizando item:', item.Item, 'allowbyClass:', item.allowbyClass)} */}
                        
                        <div className="mt-1.5">
                          <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">
                            Classes allowed:
                          </p>
                          <div className="flex flex-wrap gap-1">
                            {/* Verificar cada clase usando la misma lógica que en el filtro de clases (que sí funciona) */}
                            {(() => {
                              // Lista de todas las clases para comprobar
                              const clases = [
                                "Dark Wizard", "Dark Knight", "Fairy Elf", "Magic Gladiator", 
                                "Dark Lord", "Summoner", "Rage Fighter", "Grow Lancer", 
                                "Rune Wizard", "Slayer", "Gun Crusher", "White Wizard", 
                                "Lemuria Mage", "Illusion Knight", "Alchemist"
                              ];
                              
                              // Mapeo de nombres de clase a nombres de atributo XML
                              const classToXmlAttr: Record<string, string> = {
                                "Dark Wizard": "DarkWizard",
                                "Dark Knight": "DarkKnight",
                                "Fairy Elf": "FairyElf",
                                "Magic Gladiator": "MagicGladiator",
                                "Dark Lord": "DarkLord",
                                "Summoner": "Summoner",
                                "Rage Fighter": "RageFighter",
                                "Grow Lancer": "GrowLancer",
                                "Rune Wizard": "RuneWizard",
                                "Slayer": "Slayer",
                                "Gun Crusher": "GunCrusher",
                                "White Wizard": "LightWizard",
                                "Lemuria Mage": "LemuriaMage",
                                "Illusion Knight": "IllusionKnight",
                                "Alchemist": "Alchemist"
                              };
                              
                              // Obtener los datos del itemList.xml para este ítem
                              const cat = item.ItemCatID;
                              const index = item.ItemIndex;
                              
                              // Crear claves en diferentes formatos para buscar el ítem
                              const keys = [`${cat}.${index}`, `${cat}-${index}`, `${cat}_${index}`];
                              
                              // Clases que pueden usar este ítem
                              const clasesPermitidas: string[] = [];
                              
                              // Verificar cada clase
                              for (const clase of clases) {
                                const xmlAttr = classToXmlAttr[clase];
                                if (!xmlAttr) continue;
                                
                                // Comprobar en diferentes formatos de clave
                                for (const key of keys) {
                                  const itemXmlData = itemListData[key];
                                  if (itemXmlData) {
                                    // Buscar el atributo de clase (case insensitive)
                                    for (const itemKey in itemXmlData) {
                                      if (itemKey.toLowerCase() === xmlAttr.toLowerCase()) {
                                        const valor = parseInt(String(itemXmlData[itemKey]));
                                        if (valor > 0) {
                                          clasesPermitidas.push(clase);
                                          break;
                                        }
                                      }
                                    }
                                  }
                                }
                              }
                              
                              // Si no hay clases permitidas, mostrar mensaje
                              if (clasesPermitidas.length === 0) {
                                return (
                                  <span className="px-1.5 py-0.5 text-xs rounded-full bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-100">
                                     Used by all classes
                                  </span>
                                );
                              }
                              
                              // Mostrar las clases permitidas
                              return clasesPermitidas.map((clase, idx) => (
                                <span
                                  key={idx}
                                  className="px-1 py-0.5 text-xs rounded-full bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-100 flex items-center gap-1"
                                >
                                  <span className="w-1 h-1 rounded-full bg-green-500"></span>
                                  {clase}
                                </span>
                              ));
                            })()}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        {/* Efecto de blur progresivo usando las clases CSS */}
        {totalPages > 1 && (
          <div className="gradient-blur">
            <div></div>
            <div></div>
            <div></div>
            <div></div>
            <div></div>
            <div></div>
          </div>
        )}
        
        {/* Paginación */}
        {totalPages > 1 && (
          <div style={{ position: 'fixed', bottom: '0', left: '0', right: '0', zIndex: 50, display: 'flex', justifyContent: 'center', padding: '1.5rem' }}>
            <nav className="flex items-center gap-2 bg-gray-800/70 p-4 rounded-xl shadow-lg border border-gray-700/50">
              {/* Botón Anterior */}
              <button
                onClick={() => page > 1 && handlePageChange(page - 1)}
                disabled={page === 1}
                className={`w-10 h-10 flex items-center justify-center rounded-full transition-all duration-200 ${page === 1 ? 'opacity-50 cursor-not-allowed' : 'hover:bg-primary-600 hover:text-white'} bg-gray-700 text-gray-300`}
              >
                <ChevronLeftIcon className="h-5 w-5" />
              </button>
              
              {/* Números de página */}
              <div className="flex items-center">
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((pageNum) => {
                  // Mostrar solo página actual, primera, última y 1 o 2 páginas adyacentes
                  if (
                    pageNum === 1 ||
                    pageNum === totalPages ||
                    (pageNum >= page - 1 && pageNum <= page + 1)
                  ) {
                    return (
                      <button
                        key={pageNum}
                        onClick={() => handlePageChange(pageNum)}
                        className={`w-10 h-10 mx-1 font-medium rounded-full transition-all duration-200 ${
                          page === pageNum
                            ? 'bg-primary-600 text-white shadow-md text-sm shadow-primary-500/30 ring-2 ring-primary-400/50 transform scale-110'
                            : 'bg-gray-700 text-gray-300 hover:bg-gray-600 text-sm dark:hover:bg-gray-600 hover:text-white'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  } else if (
                    (pageNum === 2 && page > 3) ||
                    (pageNum === totalPages - 1 && page < totalPages - 2)
                  ) {
                    // Mostrar puntos suspensivos
                    return (
                      <span key={pageNum} className="mx-1 text-gray-400 dark:text-gray-500">
                        ...
                      </span>
                    );
                  }
                  return null;
                })}
              </div>
              
              {/* Botón Siguiente */}
              <button
                onClick={() => page < totalPages && handlePageChange(page + 1)}
                disabled={page === totalPages}
                className={`w-10 h-10 flex items-center justify-center rounded-full transition-all duration-200 ${page === totalPages ? 'opacity-50 cursor-not-allowed' : 'hover:bg-primary-600 hover:text-white'} bg-gray-700 text-gray-300`}
              >
                <ChevronRightIcon className="h-5 w-5" />
              </button>
            </nav>
          </div>
        )}
      </div>
    </div>
  );
}

export default App; 
